import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { delay, map, catchError } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '../../../../../environments/environment';
import { ApiMethodService } from '../../../../tools/services/api/method-api/apiMethod.service';
import { ActionBackOfficeResponse } from '../../../models/api.model';
import {
  FullTaxReportDto,
  FullTaxReportByIdDto,
  FullTaxReportQueryParams,
  PagedFullTaxResponseDto,
  UpdateFullTaxInvoicePayload,
  ConfirmFullTaxInvoicePayload,
} from '../../../models/report/fulltax-report.model';
import jsPDF from 'jspdf';
import { VatSettingService } from '../../vat-setting.service';
import { addVatToExclusive } from '../../../utils/vat-amount.util';

@Injectable({
  providedIn: 'root',
})
export class ApiFullTaxReportService {
  private readonly baseUrl = environment.apiPrivateUrl;
  private readonly http = inject(HttpClient);
  private readonly cookieService = inject(CookieService);
  private readonly apiMethodService = inject(ApiMethodService);
  private readonly vatSetting = inject(VatSettingService);

  // Toggle this if you want to force call the real API instead of local mock
  private useMock = true;

  // ================= Normal Functions =================

  /**
   * Helper function to generate a mock PDF client-side using jsPDF
   */
  private createMockPdfBlob(invoiceNo: string, customerName: string, amount: number): Blob {
    const doc = new jsPDF('p', 'mm', 'a4');

    // Add text to the PDF
    doc.setFontSize(22);
    doc.text('FULL TAX INVOICE / RECEIPT', 105, 20, { align: 'center' });
    doc.setFontSize(14);
    doc.text('(ใบกำกับภาษี / ใบเสร็จรับเงินเต็มรูป)', 105, 27, { align: 'center' });

    // Seller Info
    doc.setFontSize(10);
    doc.text('Seller: PSS Group (Thailand) Co., Ltd.', 20, 45);
    doc.text('Tax ID: 0115560017379 (Head Office)', 20, 50);
    doc.text('Address: 45/4 Moo 5, Samut Prakan, Thailand 10270', 20, 55);

    // Line Separator
    doc.line(20, 62, 190, 62);

    // Buyer Info
    doc.text(`Invoice No: ${invoiceNo}`, 120, 75);
    doc.text(`Date: ${new Date().toLocaleDateString('th-TH')}`, 120, 80);
    doc.text(`Customer: ${customerName}`, 20, 75);
    doc.text(`Address: Customer Delivery Address Line 1`, 20, 80);

    // Table Headers
    doc.line(20, 95, 190, 95);
    doc.text('No.', 22, 100);
    doc.text('Description', 40, 100);
    doc.text('Qty', 120, 100);
    doc.text('Price/Unit', 140, 100);
    doc.text('Amount', 170, 100);
    doc.line(20, 103, 190, 103);

    // Table Item
    doc.text('1', 22, 110);
    doc.text('Parking Management Service Charge', 40, 110);
    doc.text('1', 120, 110);
    doc.text(amount.toLocaleString('en-US', { minimumFractionDigits: 2 }), 140, 110);
    doc.text(amount.toLocaleString('en-US', { minimumFractionDigits: 2 }), 170, 110);

    doc.line(20, 115, 190, 115);

    // Calculation
    const { vatAmount: vat, total: grandTotal } = addVatToExclusive(amount, this.vatSetting.ratePercent());
    doc.text('Subtotal:', 130, 125);
    doc.text(amount.toLocaleString('en-US', { minimumFractionDigits: 2 }), 170, 125);
    doc.text(`VAT ${this.vatSetting.rateLabel()}%:`, 130, 130);
    doc.text(vat.toLocaleString('en-US', { minimumFractionDigits: 2 }), 170, 130);
    doc.text('Grand Total:', 130, 135);
    doc.text(grandTotal.toLocaleString('en-US', { minimumFractionDigits: 2 }), 170, 135);

    // Footnote
    doc.setFontSize(8);
    doc.text('This is a simulated PDF document generated by City Parking Full Tax Invoice System.', 105, 280, { align: 'center' });

    return doc.output('blob');
  }

  // ================= API Functions =================

  /**
   * Fetches tax invoices for displaying on the table.
   * Attempts to call the real backend API first, falling back to mock data if it fails.
   */
  getFullTaxInvoicesForTable(query: FullTaxReportQueryParams): Observable<PagedFullTaxResponseDto> {
    const token = this.cookieService.get('token');
    const params: Record<string, string | number> = {
      siteId: query.siteId,
      userId: query.userId,
      page: query.page ?? 1,
      pageSize: query.pageSize ?? 20,
    };

    if (query.startDate) params['startDate'] = query.startDate;
    if (query.endDate) params['endDate'] = query.endDate;

    console.log('Attempting to fetch full tax invoices from API...');
    return this.apiMethodService.request<PagedFullTaxResponseDto>(
      '/Backoffice/getFullTaxInvoiceForTable',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getFullTaxInvoiceForTable',
        token,
        headerOnly: false,
        params,
      }
    ).pipe(
      catchError((error) => {
        console.warn('API /Backoffice/getFullTaxInvoiceForTable failed, falling back to mock data:', error);
        return this.getMockFullTaxInvoicesForTable(query);
      })
    );
  }

  /**
   * Fetches a full tax invoice by ID.
   */
  getFullTaxInvoiceById(id?: number, receiptId?: string): Observable<FullTaxReportByIdDto> {
    const token = this.cookieService.get('token');
    const params: Record<string, string | number> = {};
    if (id) params['id'] = id;
    if (receiptId) params['receiptId'] = receiptId;

    return this.apiMethodService.request<FullTaxReportByIdDto>(
      '/Backoffice/getFullTaxInvoiceById',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getFullTaxInvoiceById',
        token,
        headerOnly: false,
        params,
      }
    ).pipe(
      catchError((error) => {
        console.warn('API /Backoffice/getFullTaxInvoiceById failed, using mock:', error);
        return of({
          id: id || 123,
          receiptId: receiptId || 'RL0126050001',
          name: receiptId || 'RL0126050001',
          customerName: 'บริษัท จำลอง จำกัด',
          createBill: new Date().toISOString(),
          createDate: new Date().toISOString(),
          status: '0',
          details: 'สมาชิกรายเดือน',
          reference: 'ABB-001234',
          remark: '',
          siteName: 'City Parking Demo',
          cardName: 'กพ9962',
          type: '',
          paymentType: 'เงินสด',
          address: '123 ถ.ตัวอย่าง กรุงเทพมหานคร 10110',
          taxId: '5123156156123',
          telephone: '0812345678',
          email: 'test@example.com',
          postcode: '10110',
          headquarters: true,
          branchNo: null,
          cpAddressId: 45,
          cpAddress: {},
          items: [],
          fee: 2000,
          vat: 140,
          total: 2140
        } as FullTaxReportByIdDto).pipe(delay(300));
      })
    );
  }

  updateFullTaxInvoice(payload: UpdateFullTaxInvoicePayload): Observable<ActionBackOfficeResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ActionBackOfficeResponse>(
      '/Backoffice/updateFullTaxInvoice',
      'PATCH',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'updateFullTaxInvoice',
        token,
        headerOnly: false,
        body: payload,
      },
    );
  }

  confirmFullTaxInvoice(payload: ConfirmFullTaxInvoicePayload): Observable<ActionBackOfficeResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ActionBackOfficeResponse>(
      '/Backoffice/confirmFullTaxInvoice',
      'PATCH',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'confirmFullTaxInvoice',
        token,
        headerOnly: false,
        body: payload,
      },
    );
  }

  /**
   * Helper method to generate and return paginated mock full tax invoices.
   */
  private getMockFullTaxInvoicesForTable(query: FullTaxReportQueryParams): Observable<PagedFullTaxResponseDto> {
    console.log('Generating mock full tax invoices database');
    const startId = 100000;

    const mockCustomerNames = [
      'บริษัท พีเอสเอส กรุ๊ป (ประเทศไทย) จำกัด',
      'บริษัท ซิตี้ ปาร์คกิ้ง จำกัด',
      'บริษัท ออโต้ เทคโซลูชั่นส์ จำกัด',
      'บริษัท ไทย ทราฟฟิก ซิสเต็มส์ จำกัด',
      'บริษัท สมาร์ท คาร์พาร์ค จำกัด',
    ];

    const mockInvoices: FullTaxReportDto[] = [];
    const baseDate = new Date();

    // Seed 100 mock invoice items
    for (let i = 0; i < 100; i++) {
      const id = startId + i;
      const custIdx = i % mockCustomerNames.length;
      const amount = Math.floor(Math.random() * 45000) + 1200;
      const vat = addVatToExclusive(amount, this.vatSetting.ratePercent()).vatAmount;
      const total = amount + vat;

      const invDate = new Date(baseDate);
      invDate.setDate(baseDate.getDate() - (i % 30));

      let status = '0';
      if (i % 4 === 0) status = '1';

      mockInvoices.push({
        id: id,
        receiptId: `RL01260500${String(i).padStart(2, '0')}`,
        customerName: mockCustomerNames[custIdx],
        createBill: invDate.toISOString(),
        siteName: 'City Parking Demo',
        fee: amount,
        vat: vat,
        total: total,
        status: status,
        details: 'สมาชิกรายเดือน'
      });
    }
    console.log('mockInvoices', mockInvoices);
    // Sorting and searching simulation...
    let filtered = [...mockInvoices];

    if (query.searchText) {
      const text = query.searchText.toLowerCase().trim();
      const field = query.searchField || 'customerName';

      filtered = filtered.filter((item: any) => {
        if (field === 'receiptId') {
          return item.receiptId.toLowerCase().includes(text);
        } else {
          return item.customerName.toLowerCase().includes(text);
        }
      });
    }

    const totalCount = filtered.length;
    const page = query.page || 1;
    const pageSize = query.pageSize || 20;
    const totalPages = Math.ceil(filtered.length / pageSize);
    const startIndex = (page - 1) * pageSize;
    const paginatedData = filtered.slice(startIndex, startIndex + pageSize);

    const response: PagedFullTaxResponseDto = {
      data: paginatedData,
      total: totalCount,
      page,
      pageSize,
      totalPages,
    };

    return of(response).pipe(delay(400));
  }

  /**
   * Generates a Full Tax Invoice PDF report.
   * Attempts to request from production API first, falling back to a client-side jsPDF if it fails.
   */
  generateFullTaxReportPdf(invoiceId: number, invoiceData: any): Observable<Blob> {
    console.log('Attempting to generate Full Tax Report PDF from API for Invoice ID:', invoiceId);

    const token = this.cookieService.get('token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });
    const url = `${this.baseUrl}/Reports/full-tax-invoice/${invoiceId}/pdf`;

    return this.http.get(url, {
      headers,
      responseType: 'blob',
    }).pipe(
      catchError((error) => {
        console.warn('API GenerateFullTaxReportPdf failed, falling back to local client-side PDF mockup:', error);
        const blob = this.createMockPdfBlob(
          invoiceData.receiptId || `INV-MOCK-${invoiceId}`,
          invoiceData.customerName || 'Customer Company Co., Ltd.',
          invoiceData.fee || 10000
        );
        return of(blob).pipe(delay(500));
      })
    );
  }
}
